import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, User } from "lucide-react";
import { api } from "../lib/api";

const WEEKDAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const PlantaoSchedule = () => {
  const [slots, setSlots] = useState([]);
  const [consultors, setConsultors] = useState([]);
  const [form, setForm] = useState({ consultor_id: "", weekday: 0, start_hour: 8, end_hour: 18 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/admin/schedule"),
        api.get("/consultant/list"),
      ]);
      setSlots(s.data); setConsultors(u.data);
      if (u.data.length && !form.consultor_id) setForm((f) => ({ ...f, consultor_id: u.data[0].id }));
    } catch { toast.error("Erro"); }
  }, [form.consultor_id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (form.end_hour <= form.start_hour) return toast.error("Hora fim deve ser depois do início.");
    setBusy(true);
    try {
      await api.post("/admin/schedule", form);
      toast.success("Slot adicionado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Excluir este slot?")) return;
    try { await api.delete(`/admin/schedule/${id}`); toast.success("Excluído"); load(); }
    catch { toast.error("Erro"); }
  };

  const byDay = WEEKDAYS.map((_, d) => slots.filter((s) => s.weekday === d));

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Governança</div>
        <h1 className="font-display font-bold text-3xl text-[#0B2B62] mt-1 flex items-center gap-3">
          <CalendarClock className="w-8 h-8 text-[#0B2B62]" /> Escala de Plantão
        </h1>
        <p className="text-slate-600 text-sm mt-2 max-w-2xl">Defina os plantões semanais dos consultores. Nos horários programados, o <b>Modo Plantão</b> do consultor ativa automaticamente (com opção de silenciar manualmente).</p>
      </div>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-xl p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="schedule-add-form">
        <div><label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Consultor</label>
          <select value={form.consultor_id} onChange={(e) => setForm({ ...form, consultor_id: e.target.value })} data-testid="schedule-consultor" className="mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm min-w-[220px]">
            {consultors.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div><label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Dia</label>
          <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: parseInt(e.target.value) })} data-testid="schedule-weekday" className="mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
            {WEEKDAYS.map((w, i) => <option key={w} value={i}>{w}</option>)}
          </select>
        </div>
        <div><label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Início</label>
          <select value={form.start_hour} onChange={(e) => setForm({ ...form, start_hour: parseInt(e.target.value) })} data-testid="schedule-start" className="mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
            {HOURS.map((h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
          </select>
        </div>
        <div><label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Fim</label>
          <select value={form.end_hour} onChange={(e) => setForm({ ...form, end_hour: parseInt(e.target.value) })} data-testid="schedule-end" className="mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
            {[...HOURS, 24].filter((h) => h > 0).map((h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
          </select>
        </div>
        <button type="submit" disabled={busy} data-testid="schedule-add-btn"
          className="inline-flex items-center gap-1.5 bg-[#0B2B62] text-white font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-60">
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </form>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="schedule-grid">
        {WEEKDAYS.map((w, di) => (
          <div key={w} className="bg-white border border-slate-200 rounded-xl p-4 min-h-[140px]" data-testid={`schedule-day-${di}`}>
            <div className="font-display font-semibold text-sm text-[#0B2B62] mb-2">{w}</div>
            {byDay[di].length === 0 ? (
              <div className="text-xs text-slate-400 italic">Sem plantão</div>
            ) : (
              <ul className="space-y-2">
                {byDay[di].map((s) => (
                  <li key={s.id} className="text-xs bg-slate-50 rounded-md p-2 flex items-start justify-between gap-2" data-testid={`schedule-slot-${s.id}`}>
                    <div className="min-w-0">
                      <div className="font-mono text-[#0B2B62] font-semibold">{String(s.start_hour).padStart(2, "0")}:00 – {String(s.end_hour).padStart(2, "0")}:00</div>
                      <div className="text-slate-600 truncate flex items-center gap-1 mt-0.5"><User className="w-3 h-3" /> {s.consultor_name}</div>
                    </div>
                    <button onClick={() => del(s.id)} className="text-red-500 hover:text-red-700 shrink-0" title="Excluir"><Trash2 className="w-3.5 h-3.5" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlantaoSchedule;
